/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import RenderGraphLoadStatus from "App/render/render_graph_load_status";
import Document from "App/render_document";
import RenderDocument from "App/render_document";
import Canvas2DRenderGraph from "App/render/canvas/render_graph";
import TexturesManager from "App/utils/textures/textures_manager";
import Messenger from "App/messenger";
import RenderObject from "App/geometry/objects/object";
import ModelStatistics from "App/model_statistics";
import View from "App/view/view";
import {vec3} from "gl-matrix";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import {DataStorage} from "App/storage/data_storage";
import IScene from "js_interface/src/scene_interface";
import {EEmbededElementTypes} from "App/model/elements/factory";

/**
 * @param {Document} aDocument
 * @class
 */
export default
class Renderer {
    private _sceneAPI: IScene;
    private _document: RenderDocument;
    private _dataStorage: DataStorage;

    // THE ORDER MATTERS, first we render the canvas 2d as we will use it for rate a webg texture for webvr mode
    private renderGraphs: {
        canvas2d: Canvas2DRenderGraph,
        webgl: WebGLRenderGraph;
    };

    private loadStatus: RenderGraphLoadStatus;

    private _resizeScheduled: boolean = false;
    private _lightingSettingsChanged: boolean = false;

    private firstRenderDone: boolean = false; // this is used for dispatching the engine is ready event for web application which can e.g. show annotations etc.

    private _texturesManager: TexturesManager;

    private currentFrameTime: number = 0.0;

    constructor(aSceneAPI: IScene, aDocument: Document) {
        this._sceneAPI = aSceneAPI;
        this._document = aDocument;
        this._dataStorage = new DataStorage(aDocument);

        // THE ORDER MATTERS, first we render the canvas 2d as we will use it for rate a webg texture for webvr mode
        this.renderGraphs = {
            canvas2d: new Canvas2DRenderGraph(this._sceneAPI, this, this._sceneAPI.getCanvas().getCanvas2DContext()),
            webgl: new WebGLRenderGraph(this._sceneAPI, this, this._sceneAPI.getCanvas().getWebGLContext()) // webgl 1
        };

        this.loadStatus = new RenderGraphLoadStatus(aSceneAPI, this);

        this._texturesManager = new TexturesManager(aSceneAPI, this, this._document.getStatistics());
    }

    public getDataStorage(): DataStorage {
        return this._dataStorage;
    }

    public getLoadStatus(): RenderGraphLoadStatus {
        return this.loadStatus;
    }

    public scheduleResize() {
        this._resizeScheduled = true;
    }

    public scheduleLightingSettingsChanged() {
        this._lightingSettingsChanged = true;
    }

    public release() {
        for (let key in this.renderGraphs) {
            this.renderGraphs[key].release();
        }

        this.firstRenderDone = false;
    }

    public getDocument(): RenderDocument {
        return this._document;
    }

    public getTexturesManager(): TexturesManager {
        return this._texturesManager;
    }

    public setGPUTexturesMemLimit(aLimitInMB: number) {
        this._texturesManager.setGPUTexturesMemLimit(aLimitInMB);
    }

    public pauseTexturesSwitching(aValue: boolean) {
        this._texturesManager.pauseTexturesSwitching(aValue);
    }

    public tick(aView: View) {
        for (let key in this.renderGraphs) {
            let renderGraph = this.renderGraphs[key];
            if (renderGraph) {
                renderGraph.tick(aView);
            }
        }

        // solve textures at the beggining
        this._texturesManager.tick(aView);
    }

    public getCurrentFrame(): number {
        return this.currentFrameTime;
    }

    public render(aView: View) {
        if (!this.firstRenderDone) {
            for (let key in this.renderGraphs) {
                this.renderGraphs[key].initialize();
            }
        }

        this.currentFrameTime = performance.now();

        for (let key in this.renderGraphs) {
            let renderGraph = this.renderGraphs[key];
            if (this._resizeScheduled) {
                renderGraph.resize(aView);
            }

            if (this._lightingSettingsChanged) {
                renderGraph.lightingSettingsChanged();
            }

            renderGraph.render(aView);
        }

        this.currentFrameTime = performance.now() - this.currentFrameTime;

        this._resizeScheduled = false;
        this._lightingSettingsChanged = false;

        if (this._sceneAPI.globe) {
            this._sceneAPI.globe.render(this);
        }

        // after first render, notify listeners that the engine is ready
        if (!this.firstRenderDone) {
            this.firstRenderDone = true;
            Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_engineIsReady", {});
        }
    }

    public renderDepth(aView: View, aProperties: any): vec3[] {
        if (!this.firstRenderDone) {
            return [];
        }

        return this.renderGraphs.webgl.renderDepth(aView, aProperties);
    }

    public renderGeometryObjectUID(aView: View, aProperties: any): any {
        if (!this.firstRenderDone) {
            return null;
        }

        return this.renderGraphs.webgl.renderGeometryObjectUID(aView, aProperties);
    }

    public renderDepthBellowCamera(aView: View, aProperties: any): any {
        if (!this.firstRenderDone) {
            return [];
        }

        return this.renderGraphs.webgl.renderDepthBellowCamera(aView, aProperties);
    }

    public renderDepthCustomCamera(aView: View, aProperties: any): any {
        if (!this.firstRenderDone) {
            return [];
        }

        return this.renderGraphs.webgl.renderDepthCustomCamera(aView, aProperties);
    }

    public renderPrimitiveId(aView: View, aProperties: any): any {
        if (!this.firstRenderDone) {
            return null;
        }

        return this.renderGraphs.webgl.renderPrimitiveId(aView, aProperties);
    }

    public viewpointChanged() {
        if (this._sceneAPI.globe !== undefined) {
            this._sceneAPI.globe.viewpointChanged();
        }

        this._dataStorage.viewpointChanged();
        this._texturesManager.viewpointChanged();

        for (let key in this.renderGraphs) {
            let renderGraph = this.renderGraphs[key];
            if (renderGraph) {
                renderGraph.viewpointChanged();
            }
        }
    }

    public notifyMeshCreated(aShape: RenderObject) {
        for (let key in this.renderGraphs) {
            this.renderGraphs[key].notifyMeshCreated(aShape);
        }

        this.recomputeGeometryLOD(); // recompute geometry lod (for non textured shapes)
    }

    /***
     * Received when texture data tiles which should be upoade to webgl texture objects are prepared.
     * @param aShapes The shapes which need the image data
     * @param aImageData Object which contains the data which should be uploaded to webgl texture object
     * @param aDiffuseTextureProperties Diffuse texture properties describes the texture object to which the image data should be uploaded
     */
    public notifySetTextureDataForShapes(aShapes: RenderObject, aImageData: any) {
        for (let key in this.renderGraphs) {
            this.renderGraphs[key].notifySetTextureDataForShapes(aShapes, aImageData);
        }
    }

    public notifySetTextureData(aUrl: string, aImageData: any) {
        for (let key in this.renderGraphs) {
            this.renderGraphs[key].notifySetTextureData(aUrl, aImageData);
        }
    }

    public addShape(aShape: RenderObject) {
        // the order matters here, because in the render graph we assign each shape the render node.
        for (let key in this.renderGraphs) {
            this.renderGraphs[key].addShape(aShape);
        }

        this._texturesManager.addShape(aShape);

        this._dataStorage.loadData(aShape);

        this._document.getStatistics().setDirty();
    }

    public updateShape(aShape: RenderObject) {
        // first we remove the modified shape from the render graph and we re-add the shape to the render graph again
        for (let key in this.renderGraphs) {
            this.renderGraphs[key].removeShape(aShape);
        }

        // add the shape with the same UID
        for (let key in this.renderGraphs) {
            this.renderGraphs[key].addShape(aShape);
        }
    }

    public updateShapeBBox(aShape: RenderObject) {
        this._dataStorage.updateShapeBBox(aShape);
        this._texturesManager.updateShapeBBox(aShape);

        for (let key in this.renderGraphs) {
            this.renderGraphs[key].updateShapeBBox(aShape);
        }
    }

    public countShapesAndUniqueTextures(aStatistics: ModelStatistics) {
        for (let key in this.renderGraphs) {
            let renderGraph = this.renderGraphs[key];
            if (renderGraph) {
                renderGraph.countShapesAndUniqueTextures(aStatistics);
            }
        }
    }

    public removeShape(aShape: RenderObject) {
        // the order matters as we first need to clear the workers and all the handlers of the shape
        // and at the end we release the shape from the render graph
        this._dataStorage.removeShape(aShape);
        this._texturesManager.removeShape(aShape);

        for (let key in this.renderGraphs) {
            this.renderGraphs[key].removeShape(aShape);
        }

        this._document.getStatistics().setDirty();
    }

    public recomputeTextureLOD() {
        this._texturesManager.recomputeTextureLOD();
    }

    public recomputeGeometryLOD() {
        //this._geometrySwitcher.recomputeGeometryLOD();
    }

    public limitTexturesResolution(aValue: number) {
        this._texturesManager.limitTexturesResolution(aValue);
    }

    public getWebGLRenderGraph(): WebGLRenderGraph {
        return this.renderGraphs.webgl;
    }

    public getCanvas2DRenderGraph(): Canvas2DRenderGraph {
        return this.renderGraphs.canvas2d;
    }

    public setViewFrustumCullingForTexturesSwitching(aValue: boolean) {
        this._texturesManager.setViewFrustumCullingForTexturesSwitching(aValue);
    }

    /**
     * Set which GeoEntity types should be rendered.
     * @param aFilter An array of EEmbededElementTypes values.
     */
    public setGeoEntitiesFilter(aFilter: EEmbededElementTypes[]) {
        this.renderGraphs.canvas2d.setGeoEntitiesFilter(aFilter);
    }
}
